import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useNavigate } from 'react-router-dom';
import critLogo from "../../assets/icons/critLogo.png";
import { CRITUTILS } from "../../config/API";
import jsPDF from 'jspdf';

const LiquorLicense = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Transaction Type
    transactionType: '',
    otherTransaction: '',
    
    // Type of License
    licenseTypes: [],
    specialEventDate: '',
    
    // Business Information
    businessName: '',
    ein: '',
    businessStreetAddress: '',
    businessMailingAddress: '',
    businessPhone: '',
    businessFax: '',
    email: '',
    dateLiquorSalesStarted: '',
    
    // Owner Contact Information
    principalOwnerName: '',
    principalOwnerMailingAddress: '',
    principalOwnerPhone: '',
    principalOwnerFax: '',
    critTribalMember: '',
    enrollmentNo: '',
    enrolledAtAnotherTribe: '',
    tribeNameIfYes: '',
    namesOfAllPersons: '',
    
    // Additional Business Information
    typeOfBusiness: '',
    businessClassification: '',
    otherBusinessType: '',
    ifCorporationStates: '',
    liquorLicenseDescription: [],
    critLicenseEinPermanently: '',
    businessLicenseInPast5Years: '',
    businessLicenseInPast5YearsBL: '',
    ownerPreviouslyApplied: '',
    ownerPreviouslyAppliedLL: '',
    ownerPreviouslyAppliedDetails: '',
    liquorLicenseSuspended: '',
    liquorLicenseSuspendedDetails: '',
    liquorLicenseSuspendedDetails: '',
    ownerOrOfficer10Percent: '',
    ownerConvictedOfFelony: '',
    ownerOrOfficerDetails: '',
    licensedByStateOrCity: '',
    liquorLicenseNumberAndType: '',
    healthAndSafetyCompliance: '',
    insuranceCarrier: ''
  });

  // Signature pad state and ref
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [currentDate] = useState(new Date().toLocaleDateString());
  const [printedName, setPrintedName] = useState('');
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submittedData, setSubmittedData] = useState(null); // Store submitted data for PDF
  const [taxpayerId, setTaxpayerId] = useState('');
  const [apiData, setApiData] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      // Check if this is a licenseTypes checkbox (by checking known values)
      const licenseTypeValues = ['class1', 'class2', 'class3', 'class4', 'class5', 'renewalClass1_5', 'renewalClass2_4', 'specialEvent', 'expeditedDelivery'];
      const liquorDescriptionValues = ['byTheDrink', 'packagedLiquor'];
      
      if (licenseTypeValues.includes(value)) {
        const currentLicenses = formData.licenseTypes;
        if (checked) {
          setFormData({
            ...formData,
            licenseTypes: [...currentLicenses, value]
          });
        } else {
          setFormData({
            ...formData,
            licenseTypes: currentLicenses.filter(item => item !== value)
          });
        }
      } else if (liquorDescriptionValues.includes(value) || name === 'liquorLicenseDescription') {
        // Single selection only: choosing one option replaces any previous selection
        if (checked) {
          setFormData({
            ...formData,
            liquorLicenseDescription: [value]
          });
        } else {
          setFormData({
            ...formData,
            liquorLicenseDescription: []
          });
        }
      }
    } else if (type === 'radio' && name === 'liquorLicenseDescription') {
      // Single selection: store as single-item array for compatibility
      setFormData({
        ...formData,
        liquorLicenseDescription: value ? [value] : []
      });
    } else {
      // Apply phone number formatting for phone fields
      if (name === 'businessPhone' || name === 'principalOwnerPhone' || name === 'businessFax' || name === 'principalOwnerFax') {
        const formattedValue = formatPhoneNumber(value);
        setFormData({
          ...formData,
          [name]: formattedValue
        });
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');

    // Limit to 10 digits
    const limited = cleaned.slice(0, 10);

    // Apply formatting based on length
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else {
      return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };

  // Format phone numbers from API
  const formatPhoneFromAPI = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    return formatPhoneNumber(cleaned);
  };

  // Fetch taxpayer data on component mount
  useEffect(() => {
    const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
    console.log("Stored Taxpayer ID:", storedTaxpayerId);

    if (storedTaxpayerId) {
      setTaxpayerId(storedTaxpayerId);
      fetchTaxpayerData(storedTaxpayerId);
    } else {
      setTaxpayerId('152');
      fetchTaxpayerData('152');
    }
  }, []);

  const fetchTaxpayerData = async (taxpayerId) => {
    try {
      setFetchLoading(true);
      const apiUrl = `${CRITUTILS}?taxpayer=${taxpayerId}&action=gettaxpayerinfo`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(result);

      if (result.Status === "Success") {
        setApiData(result.Message);
        populateFormData(result.Message);
      } else {
        console.error('Failed to fetch taxpayer data:', result.Message);
      }
    } catch (error) {
      console.error('Error fetching taxpayer data:', error);
    } finally {
      setFetchLoading(false);
    }
  };

  const populateFormData = (apiData) => {
    const { Taxpayer, BusinessLicense } = apiData;

    // Map CRIT member status
    const getCritMemberStatus = (critMember) => {
      if (critMember === 'Yes' || critMember === 'yes') return 'yes';
      if (critMember === 'No' || critMember === 'no') return 'no';
      return '';
    };

    // Map other tribe status
    const getOtherTribeStatus = (otherTribe) => {
      if (otherTribe === 'Yes' || otherTribe === 'yes') return 'yes';
      if (otherTribe === 'No' || otherTribe === 'no') return 'no';
      return '';
    };

    // Map business classification
    const getBusinessClassification = (entityType) => {
      if (!entityType) return '';
      const lower = entityType.toLowerCase();
      if (lower.includes('sole') || lower.includes('individual')) return 'soleProprietorship';
      if (lower.includes('partnership')) return 'partnership';
      if (lower.includes('corporation')) return 'corporation';
      if (lower.includes('llc') || lower.includes('limited liability company')) return 'llc';
      if (lower.includes('501') || lower.includes('non-profit')) return 'llp';
      return '';
    };

    // Helper function to build full business street address
    const buildBusinessStreetAddress = () => {
      const address = Taxpayer?.["Business Address"] || '';
      const city = Taxpayer?.["City"] || '';
      const state = Taxpayer?.["State"] || '';
      const zipCode = Taxpayer?.["Zip Code"] || '';
      
      const parts = [address, city, state, zipCode].filter(part => part.trim() !== '');
      return parts.join(', ');
    };

    setFormData(prev => ({
      ...prev,
      // Business Information
      businessName: Taxpayer?.["Business Name"] || '',
      ein: Taxpayer?.["TIN/SSN"] || '',
      businessStreetAddress: buildBusinessStreetAddress(),
      businessMailingAddress: Taxpayer?.["Owner Mailing Address"] || Taxpayer?.["Business Address "] || '',
      businessPhone: formatPhoneFromAPI(Taxpayer?.["Business Phone #"]),
      businessFax: formatPhoneFromAPI(Taxpayer?.["Business Fax #"]),
      email: Taxpayer?.["Business Email Address"] || '',
      dateLiquorSalesStarted: Taxpayer?.["Date Business to Commence"] || '',

      // Owner Contact Information
      principalOwnerName: Taxpayer?.["Owner Name(s)"] || '',
      principalOwnerMailingAddress: Taxpayer?.["Owner Mailing Address"] || Taxpayer?.["PC Mailing Address"] || '',
      principalOwnerPhone: formatPhoneFromAPI(Taxpayer?.["Owner Phone #"] || Taxpayer?.["PC Phone #"]),
      principalOwnerFax: formatPhoneFromAPI(Taxpayer?.["Owner Fax #"]),
      critTribalMember: getCritMemberStatus(Taxpayer?.["CRIT Tribal Member?"]),
      enrollmentNo: Taxpayer?.["If Yes, Enrollment #"] || '',
      enrolledAtAnotherTribe: getOtherTribeStatus(Taxpayer?.["Enrolled in Another Tribe?"]),
      tribeNameIfYes: Taxpayer?.["If Yes, Name of Tribe"] || '',
      namesOfAllPersons: Taxpayer?.["Owner Name(s)"] || '',

      // Additional Business Information
      businessClassification: getBusinessClassification(Taxpayer?.["Type Of Entity"]),
      ifCorporationStates: Taxpayer?.["If Corporation, State"] || '',
      businessLicenseInPast5Years: BusinessLicense?.["Business License Number"] ? 'yes' : '',
      businessLicenseInPast5YearsBL: BusinessLicense?.["Business License Number"] || BusinessLicense?.["If yes, BL#"] || '',
      ownerPreviouslyApplied: BusinessLicense?.["Business License Number"] ? 'yes' : '',
      ownerPreviouslyAppliedLL: BusinessLicense?.["Business License Number"] || '',
    }));
  };

  // Helper function to convert base64 data URL to File object
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
      setSignatureDataURL('');
    }
  };

  // Function to reset form to initial state
  const resetForm = () => {
    setFormData({
      // Transaction Type
      transactionType: '',
      otherTransaction: '',
      
      // Type of License
      licenseTypes: [],
      specialEventDate: '',
      
      // Business Information
      businessName: '',
      ein: '',
      businessStreetAddress: '',
      businessMailingAddress: '',
      businessPhone: '',
      businessFax: '',
      email: '',
      dateLiquorSalesStarted: '',
      
      // Owner Contact Information
      principalOwnerName: '',
      principalOwnerMailingAddress: '',
      principalOwnerPhone: '',
      principalOwnerFax: '',
      critTribalMember: '',
      enrollmentNo: '',
      enrolledAtAnotherTribe: '',
      tribeNameIfYes: '',
      namesOfAllPersons: '',
      
      // Additional Business Information
      typeOfBusiness: '',
      businessClassification: '',
      otherBusinessType: '',
      ifCorporationStates: '',
      liquorLicenseDescription: [],
      critLicenseEinPermanently: '',
      businessLicenseInPast5Years: '',
      businessLicenseInPast5YearsBL: '',
      ownerPreviouslyApplied: '',
      ownerPreviouslyAppliedLL: '',
      ownerPreviouslyAppliedDetails: '',
      liquorLicenseSuspended: '',
      liquorLicenseSuspendedDetails: '',
      ownerOrOfficer10Percent: '',
      ownerConvictedOfFelony: '',
      ownerOrOfficerDetails: '',
      licensedByStateOrCity: '',
      liquorLicenseNumberAndType: '',
      healthAndSafetyCompliance: '',
      insuranceCarrier: ''
    });
    setPrintedName('');
    clearSignature();
  };

  // Map internal radio/checkbox values to the exact UI text the user sees.
  // This keeps the UI/state logic stable (uses internal codes), while the API receives human-readable values.
  const UI_VALUE_MAPS = {
    transactionType: {
      newLicense: 'New License Application',
      renewal: 'Renewal',
      changeOwnership: 'Change in Ownership',
      newOrChangedLocation: 'New or Changed Business Location',
      other: 'Other (identify:)'
    },
    typeOfBusiness: {
      forProfit: 'For Profit',
      nonProfit: 'Non-Profit',
    },
    businessClassification: {
      soleProprietorship: 'Sole Proprietorship',
      partnership: 'Partnership',
      corporation: 'Corporation',
      llc: 'Limited Liability Company',
      llp: 'I.R.S. 501(C) Certified Organization',
      other: 'Other (Describe):',
    },
    licenseTypes: {
      class1: 'Class 1 – On Sale Retailer Of Liquor',
      class2: 'Class 2 – On-Sale Retailer Of Beer And Wine Only',
      class3: 'Class 3 – Off-Sale Retailer Of Liquor',
      class4: 'Class 4 – Off-Sale Retailer Of Beer And Wine Only',
      class5: 'Class 5 – Wholesaler',
      renewalClass1_5: 'Renewal (Class 1 / Class 5)',
      renewalClass2_4: 'Renewal (Class 2 / Class 3 / Class 4)',
      specialEvent: 'Special Event License (1 To 4 Days)',
      expeditedDelivery: 'Expedited Delivery (For Express Mail Delivery Of Your License)',
    },
    liquorLicenseDescription: {
      byTheDrink: 'By The Drink',
      packagedLiquor: 'Packaged Liquor',
    },
  };

  // API requires "Yes"/"No" (capital Y/N).
  const YES_NO_DISPLAY = { yes: 'Yes', no: 'No' };

  const FIELDS_YES_NO_UPPER = new Set([
    'critTribalMember',
    'enrolledAtAnotherTribe',
    'critLicenseEinPermanently',
    'businessLicenseInPast5Years',
    'ownerPreviouslyApplied',
    'ownerPreviouslyAppliedDetails',
    'liquorLicenseSuspended',
    'licensedByStateOrCity',
    'healthAndSafetyCompliance',
  ]);

  const FIELDS_YES_NO_TITLE = new Set([
    'ownerConvictedOfFelony',
  ]);

  const toUiValue = (fieldName, rawValue) => {
    if (rawValue == null || rawValue === '') return '';

    // Specific maps (transactionType, classification, etc.)
    const fieldMap = UI_VALUE_MAPS[fieldName];
    if (fieldMap && Object.prototype.hasOwnProperty.call(fieldMap, rawValue)) {
      return fieldMap[rawValue];
    }

    // Standard yes/no displays (always "Yes"/"No")
    if (
      (FIELDS_YES_NO_UPPER.has(fieldName) || FIELDS_YES_NO_TITLE.has(fieldName)) &&
      Object.prototype.hasOwnProperty.call(YES_NO_DISPLAY, rawValue)
    ) {
      return YES_NO_DISPLAY[rawValue];
    }

    // Fallback: send what we have.
    return rawValue;
  };

  const toUiArray = (fieldName, rawValues) => {
    const arr = Array.isArray(rawValues) ? rawValues : [];
    return arr
      .map((v) => toUiValue(fieldName, v))
      .filter((v) => v !== '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate signature
    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the application.');
      return;
    }

    // Validate printed name
    if (!printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get signature data URL
      const signatureDataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(signatureDataURL);

      // Convert signature to file
      const signatureFile = dataURLtoFile(signatureDataURL, 'signature.png');

      // Prepare JSON data
      // Convert liquorLicenseDescription array to string (comma-separated if multiple)
      const liquorLicenseDescriptionUi = toUiArray('liquorLicenseDescription', formData.liquorLicenseDescription);
      const liquorLicenseDescriptionValue =
        liquorLicenseDescriptionUi.length > 0 ? liquorLicenseDescriptionUi.join(',') : '';

      const licenseTypesUi = toUiArray('licenseTypes', formData.licenseTypes);

      const submissionData = {
        transactionType: toUiValue('transactionType', formData.transactionType),
        otherTransaction: formData.otherTransaction || '',
        businessClassification: toUiValue('businessClassification', formData.businessClassification),
        businessFax: formData.businessFax || '',
        businessLicenseInPast5Years: toUiValue('businessLicenseInPast5Years', formData.businessLicenseInPast5Years),
        businessLicenseInPast5YearsBL: formData.businessLicenseInPast5YearsBL || '',
        businessMailingAddress: formData.businessMailingAddress || '',
        businessName: formData.businessName,
        businessPhone: formData.businessPhone || '',
        businessStreetAddress: formData.businessStreetAddress,
        critLicenseEinPermanently: toUiValue('critLicenseEinPermanently', formData.critLicenseEinPermanently),
        critTribalMember: toUiValue('critTribalMember', formData.critTribalMember),
        dateLiquorSalesStarted: formData.dateLiquorSalesStarted || '',
        ein: formData.ein,
        email: formData.email || '',
        enrolledAtAnotherTribe: toUiValue('enrolledAtAnotherTribe', formData.enrolledAtAnotherTribe),
        enrollmentNo: formData.enrollmentNo || '',
        healthAndSafetyCompliance: toUiValue('healthAndSafetyCompliance', formData.healthAndSafetyCompliance),
        ifCorporationStates: formData.ifCorporationStates || '',
        insuranceCarrier: formData.insuranceCarrier || '',
        licenseTypes: licenseTypesUi,
        licensedByStateOrCity: toUiValue('licensedByStateOrCity', formData.licensedByStateOrCity),
        liquorLicenseDescription: liquorLicenseDescriptionValue,
        liquorLicenseNumberAndType: formData.liquorLicenseNumberAndType || '',
        liquorLicenseSuspended: toUiValue('liquorLicenseSuspended', formData.liquorLicenseSuspended),
        liquorLicenseSuspendedDetails: formData.liquorLicenseSuspendedDetails || '',
        namesOfAllPersons: formData.namesOfAllPersons || '',
        otherBusinessType: formData.otherBusinessType || '',
        ownerConvictedOfFelony: toUiValue('ownerConvictedOfFelony', formData.ownerConvictedOfFelony),
        ownerOrOfficer10Percent: formData.ownerOrOfficer10Percent || '',
        ownerOrOfficerDetails: formData.ownerOrOfficerDetails || '',
        ownerPreviouslyApplied: toUiValue('ownerPreviouslyApplied', formData.ownerPreviouslyApplied),
        ownerPreviouslyAppliedDetails: formData.ownerPreviouslyAppliedDetails,
        ownerPreviouslyAppliedLL: formData.ownerPreviouslyAppliedLL || '',
        principalOwnerFax: formData.principalOwnerFax || '',
        principalOwnerMailingAddress: formData.principalOwnerMailingAddress || '',
        principalOwnerName: formData.principalOwnerName,
        principalOwnerPhone: formData.principalOwnerPhone || '',
        specialEventDate: formData.specialEventDate || '',
        tribeNameIfYes: formData.tribeNameIfYes || '',
        typeOfBusiness: toUiValue('typeOfBusiness', formData.typeOfBusiness),
        printedName: printedName,
        signatureDate: currentDate
      };

      // Create FormData
      const formDataToSend = new FormData();
      formDataToSend.append('body', JSON.stringify(submissionData));
      formDataToSend.append('signature', signatureFile, 'signature.png');
      console.log('Submission Data:', JSON.stringify(submissionData));
      // API endpoint
      const apiUrl = `${CRITUTILS}?action=liquorlicense&taxpayer=${taxpayerId || '152'}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend
      });

      const responseText = await response.text();
      console.log('API Response:', responseText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, message: ${responseText}`);
      }

      // Handle response
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.warn('Response is not valid JSON, treating as plain text:', responseText);
        if (responseText.trim() === 'True' || responseText.toLowerCase().includes('success')) {
          // Store submitted data before resetting
          setSubmittedData({ ...formData, printedName, signatureDataURL, currentDate });
          setShowSuccessScreen(true);
          resetForm();
          return;
        } else {
          throw new Error(`Unexpected response from server: ${responseText}`);
        }
      }

      // Check if Status is "Success" (case-insensitive)
      if (result && (result.Status === 'Success' || result.status === 'Success' || result.status === 'success')) {
        // Store submitted data before resetting
        setSubmittedData({ ...formData, printedName, signatureDataURL, currentDate });
        setShowSuccessScreen(true);
        // Clear all form fields and signature
        resetForm();
      } else {
        // Status is not Success - show alert with Message
        alert(result.Message || result.message || 'An error occurred while submitting the application.');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      let errorMessage = 'Failed to submit application. Please try again.';
      if (error.message.includes('HTTP error')) {
        errorMessage = `Server error: ${error.message}`;
      } else if (error.message.includes('Unexpected response')) {
        errorMessage = error.message;
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setIsSigned(true);
      const sigDataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(sigDataURL);
    } else {
      setIsSigned(false);
      setSignatureDataURL('');
    }
  };

  // Success Screen Component with PDF Download
  const SuccessScreen = () => {
    const generatePDF = () => {
      if (!submittedData) {
        console.error('No submitted data available for PDF generation');
        alert('No data available for PDF generation. Please try submitting the form again.');
        return;
      }

      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 15;
      const leftMargin = 15;
      const rightMargin = pageWidth - 15;
      const contentWidth = pageWidth - 30;

      // Helper function to capitalize text
      const capitalizeText = (text) => {
        if (!text) return '';
        const str = String(text);
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
      };

      // Helper function to format date to mm/dd/yyyy (US format). For date-only strings (e.g. from
      // HTML type="date" or API YYYY-MM-DD), parse as local date to avoid UTC timezone shifting
      // the day when viewed in US timezones. Used for PDF: specialEventDate, dateLiquorSalesStarted, Submitted.
      const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
          const trimmed = String(dateString).trim();
          const dateOnly = trimmed.indexOf(' ') >= 0 ? trimmed.split(' ')[0] : trimmed;
          const parts = dateOnly.split(/[-\/]/);
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              return `${parts[1].padStart(2, '0')}/${parts[2].padStart(2, '0')}/${parts[0]}`;
            }
            return dateOnly;
          }
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return trimmed;
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();
          return `${month}/${day}/${year}`;
        } catch (e) {
          return dateString;
        }
      };

      // Helper function to get license type text
      const getLicenseTypeText = (licenseType) => {
        const licenseTypeMap = {
          'class1': 'Class 1 – On Sale Retailer Of Liquor - $1200.00',
          'class2': 'Class 2 – On-Sale Retailer Of Beer And Wine Only - $700.00',
          'class3': 'Class 3 – Off-Sale Retailer Of Liquor - $700.00',
          'class4': 'Class 4 – Off-Sale Retailer Of Beer And Wine Only - $700.00',
          'class5': 'Class 5 – Wholesaler - $1200.00',
          'renewalClass1_5': 'Renewal (Class 1 / Class 5) - $185.00',
          'renewalClass2_4': 'Renewal (Class 2 / Class 3 / Class 4) - $110.00',
          'specialEvent': 'Special Event License (1 To 4 Days) - $35.00',
          'expeditedDelivery': 'Expedited Delivery - $15.00'
        };
        return licenseTypeMap[licenseType] || licenseType;
      };

      // Helper function to get transaction type text
      const getTransactionTypeText = (type) => {
        const transactionTypeMap = {
          'newLicense': 'New License Application',
          'renewal': 'Renewal',
          'changeOwnership': 'Change in Ownership',
          'newOrChangedLocation': 'New or Changed Business Location',
          'other': 'Other'
        };
        return transactionTypeMap[type] || type;
      };

      // Helper function to format business classification
      const formatBusinessClassification = (classification) => {
        if (!classification) return '';
        const classificationMap = {
          'soleProprietorship': 'Sole Proprietorship',
          'partnership': 'Partnership',
          'corporation': 'Corporation',
          'llc': 'Limited Liability Company',
          'llp': 'I.R.S. 501(C) Certified Organization',
          'other': 'Other'
        };
        return classificationMap[classification] || capitalizeText(classification);
      };

      // Helper function to format liquor license description
      const formatLiquorDescription = (description) => {
        if (!description) return '';
        if (Array.isArray(description)) {
          return description.map(desc => {
            if (desc === 'byTheDrink') return 'By The Drink';
            if (desc === 'packagedLiquor') return 'Packaged Liquor';
            return desc;
          }).join(', ');
        }
        if (typeof description === 'string') {
          return description.split(',').map(desc => {
            const trimmed = desc.trim();
            if (trimmed === 'byTheDrink') return 'By The Drink';
            if (trimmed === 'packagedLiquor') return 'Packaged Liquor';
            return trimmed;
          }).join(', ');
        }
        return description;
      };

      // Helper function to add section header - attractive and professional
      const addSectionHeader = (title) => {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = 15;
        }
        yPosition += 8; // Space before section
        
        // Subtle background bar for section header
        pdf.setFillColor(245, 245, 250);
        pdf.rect(leftMargin, yPosition - 2, contentWidth, 7, 'F');
        
        // Colored accent line
        pdf.setFillColor(132, 0, 40);
        pdf.rect(leftMargin, yPosition - 2, 3, 7, 'F');
        
        // Section title
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(50, 50, 50);
        pdf.text(title, leftMargin + 6, yPosition + 3);
        
        // Bottom border
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.5);
        pdf.line(leftMargin, yPosition + 5, rightMargin, yPosition + 5);
        
        yPosition += 8; // Space after header
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
      };

      // Helper function to add field - attractive and clean
      const addField = (label, value, required = false) => {
        // Check if we need a new page before adding field
        if (yPosition > pageHeight - 25) {
          pdf.addPage();
          yPosition = 15;
        }
        
        if (value || required) {
          // Calculate content height
          const valueText = value || 'N/A';
          const valueLines = pdf.splitTextToSize(valueText, contentWidth - 6);
          const fieldHeight = Math.max(10, (valueLines.length * 5) + 10);
          
          // Subtle background for field
          pdf.setFillColor(252, 252, 255);
          pdf.rect(leftMargin, yPosition - 3, contentWidth, fieldHeight, 'F');
          
          // Add label
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(9);
          pdf.setTextColor(60, 60, 60);
          const labelText = `${label}:`;
          pdf.text(labelText, leftMargin + 2, yPosition + 2);
          yPosition += 8; // Increased space between question and answer
          
          // Add value
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.setTextColor(0, 0, 0);
          valueLines.forEach((line) => {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 15;
            }
            pdf.text(line, leftMargin + 4, yPosition);
            yPosition += 5;
          });
          
          yPosition += 4; // Space after field
        }
      };

      // Attractive Professional Header
      // Header background
      pdf.setFillColor(132, 0, 40);
      pdf.rect(0, 0, pageWidth, 32, 'F');
      
      // White text on colored background
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(15);
      pdf.setFont('helvetica', 'bold');
      pdf.text('COLORADO RIVER INDIAN TRIBES', pageWidth / 2, 12, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.text('Liquor Control Board', pageWidth / 2, 20, { align: 'center' });
      
      // Bottom border of header
      pdf.setDrawColor(180, 0, 50);
      pdf.setLineWidth(1);
      pdf.line(0, 32, pageWidth, 32);
      
      // Document title below header
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Application For Liquor License - Summary', pageWidth / 2, 40, { align: 'center' });
      
      // Subtle divider line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(leftMargin, 45, rightMargin, 45);
      
      yPosition = 50;

      // Helper: get value as sent to API (toUiValue for transformed fields, raw otherwise)
      const apiVal = (fieldName, rawValue) => (fieldName ? toUiValue(fieldName, rawValue) : rawValue) || '';

      // 1. TRANSACTION TYPE (same order as form)
      addSectionHeader('1. TRANSACTION TYPE');
      addField('Transaction Type', apiVal('transactionType', submittedData.transactionType), true);
      if (submittedData.transactionType === 'other' && submittedData.otherTransaction) {
        addField('Other (identify:)', submittedData.otherTransaction);
      }

      // 2. TYPE OF LICENSE REQUESTED (same order as form)
      addSectionHeader('2. TYPE OF LICENSE REQUESTED');
      yPosition += 3; // Add space above where answers start
      if (submittedData.licenseTypes && submittedData.licenseTypes.length > 0) {
        submittedData.licenseTypes.forEach((licenseType) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = 15;
          }
          // Use API value (toUiValue for licenseTypes)
          const licenseText = apiVal('licenseTypes', licenseType);
          const licenseLines = pdf.splitTextToSize(licenseText, contentWidth - 10);
          const itemHeight = Math.max(8, licenseLines.length * 5 + 4);
          
          pdf.setFillColor(250, 250, 252);
          pdf.rect(leftMargin + 2, yPosition - 2, contentWidth - 4, itemHeight, 'F');
          
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.setTextColor(0, 0, 0);
          licenseLines.forEach((line, index) => {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 15;
            }
            // Colored bullet point
            pdf.setFillColor(132, 0, 40);
            pdf.circle(leftMargin + 4, yPosition - 1, 1.5, 'F');
            pdf.setTextColor(0, 0, 0);
            pdf.text(line, leftMargin + 8, yPosition);
            yPosition += 5;
          });
          
          if (licenseType === 'specialEvent' && submittedData.specialEventDate) {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 15;
            }
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8.5);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`Indicate date when special event starts: ${formatDate(submittedData.specialEventDate)}`, leftMargin + 8, yPosition);
            yPosition += 5;
          }
          yPosition += 3; // Space between license types
        });
        yPosition += 2;
      } else {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 15;
        }
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        pdf.text('No license types selected', leftMargin, yPosition);
        yPosition += 6;
      }

      // 3. BUSINESS INFORMATION (same order as form, fields 1–8)
      addSectionHeader('3. BUSINESS INFORMATION');
      addField('1. Business Name', submittedData.businessName, true);
      addField('2. Federal Employer Identification Number (EIN)', submittedData.ein, true);
      addField('3. Business Street Address (Including City, State And Zip Code)', submittedData.businessStreetAddress, true);
      addField('4. Business Mailing Address (If Different)', submittedData.businessMailingAddress);
      addField('5. Business Telephone Number', submittedData.businessPhone);
      addField('6. Business Fax Number', submittedData.businessFax);
      addField('7. Email Address', submittedData.email);
      addField('8. Date Liquor Sales Started', formatDate(submittedData.dateLiquorSalesStarted));

      // 4. OWNER, CONTACT PERSON OR AGENT CONTACT INFORMATION (same order as form, fields 9–15)
      addSectionHeader('4. OWNER, CONTACT PERSON OR AGENT CONTACT INFORMATION');
      addField('9. Principal Owner Name (Or, If Corporation, Contact Person Or Agent)', submittedData.principalOwnerName, true);
      addField('10. Principal Owner Mailing Address', submittedData.principalOwnerMailingAddress);
      addField('11. Principal Owner Telephone Number', submittedData.principalOwnerPhone);
      addField('12. Principal Owner Fax Number', submittedData.principalOwnerFax);
      addField('13. CRIT Tribal Member?', apiVal('critTribalMember', submittedData.critTribalMember));
      if (submittedData.critTribalMember === 'yes') {
        addField('If Yes, Enrollment No.', submittedData.enrollmentNo);
      }
      addField('14. Enrolled At Another Indian Tribe?', apiVal('enrolledAtAnotherTribe', submittedData.enrolledAtAnotherTribe));
      if (submittedData.enrolledAtAnotherTribe === 'yes') {
        addField('If Yes, Name Of Tribe', submittedData.tribeNameIfYes);
      }
      addField('15. Names Of All Persons With An Economic Interest In The Business, Including The Nature And Extent Of Interest', submittedData.namesOfAllPersons);

      // 5. ADDITIONAL BUSINESS INFORMATION (same order as form, fields 16–26)
      addSectionHeader('5. ADDITIONAL BUSINESS INFORMATION');
      addField('16. Type Of Business', apiVal('typeOfBusiness', submittedData.typeOfBusiness));
      addField('17. Business Classification', apiVal('businessClassification', submittedData.businessClassification));
      if (submittedData.businessClassification === 'other') {
        addField('Other (Describe)', submittedData.otherBusinessType);
      }
      if (submittedData.businessClassification === 'corporation') {
        addField('A. If Corporation, Indicate State(S) Or Tribe(S) Where Incorporated', submittedData.ifCorporationStates);
      }
      addField('18. Liquor License Description', formatLiquorDescription(submittedData.liquorLicenseDescription));
      addField('19. CRIT License EIN For Business Permanently?', apiVal('critLicenseEinPermanently', submittedData.critLicenseEinPermanently));
      addField('20. Has The Business Been Issued A CRIT Business License In The Past 5 Yrs?', apiVal('businessLicenseInPast5Years', submittedData.businessLicenseInPast5Years));
      if (submittedData.businessLicenseInPast5Years === 'yes') {
        addField('If Yes, BL#', submittedData.businessLicenseInPast5YearsBL);
      }
      addField('21. Has The Owner Ever Been Issued A CRIT Liquor License In The Past 5 Yrs?', apiVal('ownerPreviouslyApplied', submittedData.ownerPreviouslyApplied));
      if (submittedData.ownerPreviouslyApplied === 'yes') {
        addField('If Yes, LL #', submittedData.ownerPreviouslyAppliedLL);
      }
      addField('22. Has The Principal Owner Previously Applied For A CRIT Liquor License Under Another Name?', apiVal('ownerPreviouslyAppliedDetails', submittedData.ownerPreviouslyAppliedDetails));
      addField('23. Has Any Liquor License Held Ever Been Suspended, Revoked Or Denied?', apiVal('liquorLicenseSuspended', submittedData.liquorLicenseSuspended));
      if (submittedData.liquorLicenseSuspended === 'yes' && submittedData.liquorLicenseSuspendedDetails) {
        addField('If yes, provide details', submittedData.liquorLicenseSuspendedDetails);
      }
      addField('24. Has Any Owner Or Officers, Directors And Stockholders Who Own 10% Or More Of The Business Been Convicted Of A Felony In The Past 5 Years?', apiVal('ownerConvictedOfFelony', submittedData.ownerConvictedOfFelony));
      if (submittedData.ownerConvictedOfFelony === 'yes') {
        addField('If yes, explain (List the names and details)', submittedData.ownerOrOfficerDetails || submittedData.ownerOrOfficer10Percent);
      }
      addField('25. Are You Licensed By Any State Or City Government?', apiVal('licensedByStateOrCity', submittedData.licensedByStateOrCity));
      if (submittedData.licensedByStateOrCity === 'yes') {
        addField('If yes, enter Liquor License Number and Type', submittedData.liquorLicenseNumberAndType);
      }
      addField('26. Are You In Compliance With The Tribes\' Health And Safety Code Section 2-408(B)?', apiVal('healthAndSafetyCompliance', submittedData.healthAndSafetyCompliance));
      if (submittedData.healthAndSafetyCompliance === 'yes') {
        addField('If yes, enter name of insurance carrier', submittedData.insuranceCarrier);
      }

      // 6. CONSENT AND VERIFICATION (same order as form)
      addSectionHeader('6. CONSENT AND VERIFICATION');
      addField('Printed Name', submittedData.printedName, true);
      addField('Date Signed', submittedData.currentDate, true);

      if (submittedData.signatureDataURL) {
        try {
          // Check if we need a new page before signature
          if (yPosition > pageHeight - 50) {
            pdf.addPage();
            yPosition = 15;
          }
          yPosition += 3;
          
          // Signature container with subtle background
          const sigBoxHeight = 32;
          pdf.setFillColor(252, 252, 255);
          pdf.rect(leftMargin, yPosition, contentWidth, sigBoxHeight, 'F');
          
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(9);
          pdf.setTextColor(60, 60, 60);
          pdf.text('Applicant Signature:', leftMargin + 3, yPosition + 5);
          yPosition += 8;
          
          // Check if signature image fits on current page
          if (yPosition + 30 > pageHeight - 30) {
            pdf.addPage();
            yPosition = 15;
            // Redraw background
            pdf.setFillColor(252, 252, 255);
            pdf.rect(leftMargin, yPosition, contentWidth, sigBoxHeight, 'F');
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.text('Applicant Signature:', leftMargin + 3, yPosition + 5);
            yPosition += 8;
          }
          
          // Add signature image with attractive border
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.5);
          pdf.rect(leftMargin + 3, yPosition, 70, 22, 'S');
          pdf.addImage(submittedData.signatureDataURL, 'PNG', leftMargin + 5, yPosition + 1, 66, 20);
          yPosition += 26;
        } catch (error) {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = 15;
          }
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.setTextColor(200, 0, 0);
          pdf.text('Applicant Signature: Provided (image error)', leftMargin, yPosition);
          yPosition += 6;
        }
      } else {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 15;
        }
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Applicant Signature: Not provided', leftMargin, yPosition);
        yPosition += 6;
      }

      // Attractive Footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        
        // Footer background
        pdf.setFillColor(250, 250, 250);
        pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
        
        // Footer line
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.line(leftMargin, pageHeight - 15, rightMargin, pageHeight - 15);
        
        // Colored accent line
        pdf.setFillColor(132, 0, 40);
        pdf.rect(leftMargin, pageHeight - 15, 20, 1, 'F');
        
        // Page number
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
        
        // Submission date on last page
        if (i === totalPages) {
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(132, 0, 40);
          pdf.setFontSize(8);
          pdf.text(`Submitted: ${formatDate(new Date())}`, rightMargin, pageHeight - 8, { align: 'right' });
        }
      }

      // Save the PDF
      const fileName = `CRIT_Liquor_License_Application_${submittedData.businessName || 'application'}_${new Date().getFullYear()}.pdf`;
      pdf.save(fileName);
    };

    const handleClose = () => {
      // Close the current tab
      window.close();

      // Fallback - if window.close doesn't work, go to home
      setTimeout(() => {
        navigate('/home');
      }, 100);
    };

    return (
      <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200 text-center">
          <div className="bg-primary text-white p-6">
            <h1 className="text-2xl font-bold">Colorado River Indian Tribes</h1>
            <h2 className="text-xl font-semibold mt-1">Liquor Control Board</h2>
          </div>

          <div className="p-8">
            <div className="flex justify-center mb-6">
              <svg className="h-16 w-16 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>

            <h3 className="text-2xl font-bold text-gray-800 mb-4">Application Submitted Successfully!</h3>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 font-semibold">
                Your liquor license application has been submitted successfully.
              </p>

              <p className="text-green-700 text-sm">
                Reference: <span className="font-bold">Liquor License Application</span>
              </p>
            </div>

            <p className="text-gray-600 mb-6 text-sm">
              Thank you for submitting your liquor license application.
              Your application has been received and is being processed.
              You can download a detailed PDF copy for your records.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={generatePDF}
                className="bg-blue-600 text-white px-2 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 shadow-md flex items-center justify-center gap-2"
              >
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Application PDF
              </button>

              <button
                onClick={handleClose}
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition duration-200 shadow-md"
              >
                Close
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              PDF includes all application details with signatures and field values.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // If showing success screen, return only the success screen
  if (showSuccessScreen) {
    return <SuccessScreen />;
  }

  // Loading State
  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your business information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="bg-primary text-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src={critLogo} alt="CRIT Logo" className="w-20 h-20 rounded-full bg-white p-2" />
            <div className="text-center">
              <h1 className="text-3xl font-bold mb-1">Colorado River Indian Tribes</h1>
              <h2 className="text-2xl font-semibold mb-1">Liquor Control Board</h2>
              <h3 className="text-xl font-semibold">Application For Liquor License</h3>
            </div>
          </div>
          <div className="bg-primary rounded-lg p-4 mt-4">
            <p className="text-sm leading-relaxed">
              <strong>Notice:</strong> Application must be completed in its entirety and signed. No modifications of any kind may be made. 
              <strong> Payments Received Are Non-Refundable.</strong> Please read entire License before completing this Application. Please type or write legibly.
            </p>
          </div>
        </div>

        {/* Data Loaded Notice */}
        {apiData && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-blue-800 font-semibold">
                Your existing business information has been loaded. Please review and update any necessary fields.
              </p>
            </div>
          </div>
        )}

        {/* Transaction Type */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-6">
          <h3 className="font-bold text-lg mb-4 text-primary border-b-2 border-primary pb-2">Transaction Type (Check One Only)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="transactionType"
                  value="newLicense"
                  checked={formData.transactionType === 'newLicense'}
                  onChange={handleChange}
                  className="w-4 h-4"
                />
                <span className="text-sm">New License Application</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="transactionType"
                  value="renewal"
                  checked={formData.transactionType === 'renewal'}
                  onChange={handleChange}
                  className="w-4 h-4"
                />
                <span className="text-sm">Renewal</span>
              </label>
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="transactionType"
                  value="changeOwnership"
                  checked={formData.transactionType === 'changeOwnership'}
                  onChange={handleChange}
                  className="w-4 h-4"
                />
                <span className="text-sm">Change in Ownership</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="transactionType"
                  value="newOrChangedLocation"
                  checked={formData.transactionType === 'newOrChangedLocation'}
                  onChange={handleChange}
                  className="w-4 h-4"
                />
                <span className="text-sm">New or Changed Business Location</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="transactionType"
                  value="other"
                  checked={formData.transactionType === 'other'}
                  onChange={handleChange}
                  className="w-4 h-4"
                />
                <span className="text-sm">Other (identify:)</span>
                <input
                  type="text"
                  name="otherTransaction"
                  value={formData.otherTransaction}
                  onChange={handleChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  disabled={formData.transactionType !== 'other'}
                />
                <span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Type of License */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-6">
          <h3 className="font-bold text-lg mb-4 text-primary border-b-2 border-primary pb-2">Type Of License Requested (Check All That Apply)</h3>
          <div className="space-y-3">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                value="class1"
                checked={formData.licenseTypes.includes('class1')}
                onChange={handleChange}
                className="w-4 h-4 mt-1"
              />
              <span className="text-sm flex-1">
                <strong>Class 1 – On Sale Retailer Of Liquor</strong>
                <span className="float-right">Fee: $ <strong>1200.00</strong> (includes $200 application fee)</span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                value="class2"
                checked={formData.licenseTypes.includes('class2')}
                onChange={handleChange}
                className="w-4 h-4 mt-1"
              />
              <span className="text-sm flex-1">
                <strong>Class 2 – On-Sale Retailer Of Beer And Wine Only</strong>
                <span className="float-right">Fee: $ <strong>700.00</strong> (includes $200 application fee)</span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                value="class3"
                checked={formData.licenseTypes.includes('class3')}
                onChange={handleChange}
                className="w-4 h-4 mt-1"
              />
              <span className="text-sm flex-1">
                <strong>Class 3 – Off-Sale Retailer Of Liquor</strong>
                <span className="float-right">Fee: $ <strong>700.00</strong> (includes $200 application fee)</span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                value="class4"
                checked={formData.licenseTypes.includes('class4')}
                onChange={handleChange}
                className="w-4 h-4 mt-1"
              />
              <span className="text-sm flex-1">
                <strong>Class 4 – Off-Sale Retailer Of Beer And Wine Only</strong>
                <span className="float-right">Fee: $ <strong>700.00</strong> (includes $200 application fee)</span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                value="class5"
                checked={formData.licenseTypes.includes('class5')}
                onChange={handleChange}
                className="w-4 h-4 mt-1"
              />
              <span className="text-sm flex-1">
                <strong>Class 5 – Wholesaler</strong>
                <span className="float-right">Fee: $ <strong>1200.00</strong> (includes $200 application fee)</span>
              </span>
            </label>
            <div className="border-t border-gray-400 pt-2 mt-2 space-y-3">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  value="renewalClass1_5"
                  checked={formData.licenseTypes.includes('renewalClass1_5')}
                  onChange={handleChange}
                  className="w-4 h-4 mt-1"
                />
                <span className="text-sm flex-1">
                  <strong>Renewal (Class 1 / Class 5)</strong>
                  <span className="float-right">Fee: $ <strong>185.00</strong> (includes $35 application fee)</span>
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  value="renewalClass2_4"
                  checked={formData.licenseTypes.includes('renewalClass2_4')}
                  onChange={handleChange}
                  className="w-4 h-4 mt-1"
                />
                <span className="text-sm flex-1">
                  <strong>Renewal (Class 2 / Class 3 / Class 4)</strong>
                  <span className="float-right">Fee: $ <strong>110.00</strong> (includes $35 application fee)</span>
                </span>
              </label>
            </div>
            <div className="border-t border-gray-400 pt-2 mt-2">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  value="specialEvent"
                  checked={formData.licenseTypes.includes('specialEvent')}
                  onChange={handleChange}
                  className="w-4 h-4 mt-1"
                />
                <span className="text-sm flex-1">
                  <strong>Special Event License (1 To 4 Days)</strong>
                  <span className="float-right">Fee: $ <strong>35.00</strong> (includes $10 application fee)</span>
                  <br />
                  <span className="text-xs">Indicate date when special event starts: </span>
                  <input
                    type="date"
                    name="specialEventDate"
                    value={formData.specialEventDate}
                    onChange={handleChange}
                    className="border-b border-gray-800 text-xs ml-2"
                    disabled={!formData.licenseTypes.includes('specialEvent')}
                  />
                  <span className="text-xs ml-2">(mm/dd/yyyy)</span>
                </span>
              </label>
            </div>
            <div className="border-t border-gray-400 pt-2 mt-2">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  value="expeditedDelivery"
                  checked={formData.licenseTypes.includes('expeditedDelivery')}
                  onChange={handleChange}
                  className="w-4 h-4 mt-1"
                />
                <span className="text-sm flex-1">
                  <strong>Expedited Delivery (For Express Mail Delivery Of Your License)</strong>
                  <span className="float-right">Additional Fee: $ <strong>15.00</strong></span>
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-6">
          <h3 className="font-bold text-lg mb-4 text-white bg-primary rounded-lg p-3">Business Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 block mb-2">1. Business Name:</label>
              <input
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 block mb-2">2. Federal Employer Identification Number (EIN):</label>
              <input
                type="text"
                name="ein"
                value={formData.ein}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700 block mb-2">3. Business Street Address (Including City, State And Zip Code):</label>
              <input
                type="text"
                name="businessStreetAddress"
                value={formData.businessStreetAddress}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-sm font-semibold text-gray-700 block mb-2">4. Business Mailing Address (If Different):</label>
              <input
                type="text"
                name="businessMailingAddress"
                value={formData.businessMailingAddress}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-sm font-semibold text-gray-700 block mb-2">5. Business Telephone Number:</label>
              <input
                type="tel"
                name="businessPhone"
                value={formData.businessPhone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-sm font-semibold text-gray-700 block mb-2">6. Business Fax Number:</label>
              <input
                type="tel"
                name="businessFax"
                value={formData.businessFax}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-sm font-semibold text-gray-700 block mb-2">7. Email Address:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-sm font-semibold text-gray-700 block mb-2">8. Date Liquor Sales Started:</label>
              <input
                type="date"
                name="dateLiquorSalesStarted"
                value={formData.dateLiquorSalesStarted}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Owner Contact Information */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-6">
          <h3 className="font-bold text-lg mb-4 text-white bg-primary rounded-lg p-3">Owner, Contact Person Or Agent Contact Information</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">9. Principal Owner Name (Or, If Corporation, Contact Person Or Agent):</label>
              <input
                type="text"
                name="principalOwnerName"
                value={formData.principalOwnerName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">10. Principal Owner Mailing Address:</label>
              <input
                type="text"
                name="principalOwnerMailingAddress"
                value={formData.principalOwnerMailingAddress}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">11. Principal Owner Telephone Number:</label>
              <input
                type="tel"
                name="principalOwnerPhone"
                value={formData.principalOwnerPhone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">12. Principal Owner Fax Number:</label>
              <input
                type="tel"
                name="principalOwnerFax"
                value={formData.principalOwnerFax}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">13. CRIT Tribal Member?</label>
              <div className="mt-2 flex gap-4 items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="critTribalMember"
                    value="yes"
                    checked={formData.critTribalMember === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">YES</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="critTribalMember"
                    value="no"
                    checked={formData.critTribalMember === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">NO</span>
                </label>
                {formData.critTribalMember === 'yes' && (
                  <>
                    <span className="text-sm">If Yes, Enrollment No.:</span>
                    <input
                      type="text"
                      name="enrollmentNo"
                      value={formData.enrollmentNo}
                      onChange={handleChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">14. Enrolled At Another Indian Tribe?</label>
              <div className="mt-2 flex gap-4 items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="enrolledAtAnotherTribe"
                    value="yes"
                    checked={formData.enrolledAtAnotherTribe === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">YES</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="enrolledAtAnotherTribe"
                    value="no"
                    checked={formData.enrolledAtAnotherTribe === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">NO</span>
                </label>
                {formData.enrolledAtAnotherTribe === 'yes' && (
                  <>
                    <span className="text-sm">If Yes, Name Of Tribe:</span>
                    <input
                      type="text"
                      name="tribeNameIfYes"
                      value={formData.tribeNameIfYes}
                      onChange={handleChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">15. Names Of All Persons With An Economic Interest In The Business, Including The Nature And Extent Of Interest:</label>
              <textarea
                name="namesOfAllPersons"
                value={formData.namesOfAllPersons}
                onChange={handleChange}
                rows="3"
                className="w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mt-1 p-3"
              />
            </div>
          </div>
        </div>

        {/* Additional Business Information */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-6">
          <h3 className="font-bold text-lg mb-4 text-white bg-primary rounded-lg p-3">Additional Business Information</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">16. Type Of Business:</label>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="typeOfBusiness"
                    value="forProfit"
                    checked={formData.typeOfBusiness === 'forProfit'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">For Profit</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="typeOfBusiness"
                    value="nonProfit"
                    checked={formData.typeOfBusiness === 'nonProfit'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Non-Profit</span>
                </label>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">17. Business Classification:</label>
              <div className="mt-2 space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="businessClassification"
                    value="soleProprietorship"
                    checked={formData.businessClassification === 'soleProprietorship'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Sole Proprietorship</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="businessClassification"
                    value="partnership"
                    checked={formData.businessClassification === 'partnership'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Partnership</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="businessClassification"
                    value="corporation"
                    checked={formData.businessClassification === 'corporation'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Corporation</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="businessClassification"
                    value="llc"
                    checked={formData.businessClassification === 'llc'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Limited Liability Company</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="businessClassification"
                    value="llp"
                    checked={formData.businessClassification === 'llp'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">I.R.S. 501(C) Certified Organization</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="businessClassification"
                    value="other"
                    checked={formData.businessClassification === 'other'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Other (Describe):</span>
                  <input
                    type="text"
                    name="otherBusinessType"
                    value={formData.otherBusinessType}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    disabled={formData.businessClassification !== 'other'}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">A. If Corporation, Indicate State(S) Or Tribe(S) Where Incorporated:</label>
              <input
                type="text"
                name="ifCorporationStates"
                value={formData.ifCorporationStates}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">18. Liquor License Description:</label>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="liquorLicenseDescription"
                    value="byTheDrink"
                    checked={formData.liquorLicenseDescription.includes('byTheDrink')}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">By The Drink</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="liquorLicenseDescription"
                    value="packagedLiquor"
                    checked={formData.liquorLicenseDescription.includes('packagedLiquor')}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Packaged Liquor</span>
                </label>
              </div>
             
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">19. CRIT License EIN For Business Permanently?</label>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="critLicenseEinPermanently"
                    value="yes"
                    checked={formData.critLicenseEinPermanently === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">YES</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="critLicenseEinPermanently"
                    value="no"
                    checked={formData.critLicenseEinPermanently === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">NO</span>
                </label>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">20. Has The Business Been Issued A CRIT Business License In The Past 5 Yrs?</label>
              <div className="mt-2 flex gap-4 items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="businessLicenseInPast5Years"
                    value="yes"
                    checked={formData.businessLicenseInPast5Years === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">YES</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="businessLicenseInPast5Years"
                    value="no"
                    checked={formData.businessLicenseInPast5Years === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">NO</span>
                </label>
                {formData.businessLicenseInPast5Years === 'yes' && (
                  <>
                    <span className="text-sm">If Yes, BL#:</span>
                    <input
                      type="text"
                      name="businessLicenseInPast5YearsBL"
                      value={formData.businessLicenseInPast5YearsBL}
                      onChange={handleChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">21. Has The Owner Ever Been Issued A CRIT Liquor License In The Past 5 Yrs?</label>
              <div className="mt-2 flex gap-4 items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ownerPreviouslyApplied"
                    value="yes"
                    checked={formData.ownerPreviouslyApplied === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">YES</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ownerPreviouslyApplied"
                    value="no"
                    checked={formData.ownerPreviouslyApplied === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">NO</span>
                </label>
                {formData.ownerPreviouslyApplied === 'yes' && (
                  <>
                    <span className="text-sm">If Yes, LL #:</span>
                    <input
                      type="text"
                      name="ownerPreviouslyAppliedLL"
                      value={formData.ownerPreviouslyAppliedLL}
                      onChange={handleChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">22. Has The Principal Owner Previously Applied For A CRIT Liquor License Under Another Name?</label>
              <div className="mt-2 flex gap-4 items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ownerPreviouslyAppliedDetails"
                    value="yes"
                    checked={formData.ownerPreviouslyAppliedDetails === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">YES</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ownerPreviouslyAppliedDetails"
                    value="no"
                    checked={formData.ownerPreviouslyAppliedDetails === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">NO</span>
                </label>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">23. Has Any Liquor License Held Ever Been Suspended, Revoked Or Denied?</label>
              <div className="mt-2 flex gap-4 items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="liquorLicenseSuspended"
                    value="yes"
                    checked={formData.liquorLicenseSuspended === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">YES</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="liquorLicenseSuspended"
                    value="no"
                    checked={formData.liquorLicenseSuspended === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">NO</span>
                </label>
              </div>
              {formData.liquorLicenseSuspended === 'yes' && (
                <input
                  type="text"
                  name="liquorLicenseSuspendedDetails"
                  value={formData.liquorLicenseSuspendedDetails}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                  placeholder="If yes, provide details"
                />
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">24. Has Any Owner Or Officers, Directors And Stockholders Who Own 10% Or More Of The Business Been Convicted Of A Felony In The Past 5 Years?</label>
              <div className="mt-2 flex gap-4 items-start">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ownerConvictedOfFelony"
                    value="yes"
                    checked={formData.ownerConvictedOfFelony === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Yes</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ownerConvictedOfFelony"
                    value="no"
                    checked={formData.ownerConvictedOfFelony === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">No</span>
                </label>
              </div>
              {formData.ownerConvictedOfFelony === 'yes' && (
                <textarea
                  name="ownerOrOfficer10Percent"
                  value={formData.ownerOrOfficer10Percent}
                  onChange={handleChange}
                  rows="2"
                  className="w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mt-2 p-3"
                  placeholder="If yes, explain (List the names and details)"
                />
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">25. Are You Licensed By Any State Or City Government?</label>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="licensedByStateOrCity"
                    value="yes"
                    checked={formData.licensedByStateOrCity === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">YES</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="licensedByStateOrCity"
                    value="no"
                    checked={formData.licensedByStateOrCity === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">NO</span>
                </label>
              </div>
              <input
                type="text"
                name="liquorLicenseNumberAndType"
                value={formData.liquorLicenseNumberAndType}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                placeholder="If yes, enter Liquor License Number and Type"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">26. Are You In Compliance With The Tribes' Health And Safety Code Section 2-408(B)?</label>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="healthAndSafetyCompliance"
                    value="yes"
                    checked={formData.healthAndSafetyCompliance === 'yes'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">YES</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="healthAndSafetyCompliance"
                    value="no"
                    checked={formData.healthAndSafetyCompliance === 'no'}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">NO</span>
                </label>
              </div>
              <input
                type="text"
                name="insuranceCarrier"
                value={formData.insuranceCarrier}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                placeholder="If yes, enter name of insurance carrier"
              />
            </div>
          </div>
        </div>

        {/* Consent and Verification */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-6">
          <h3 className="font-bold text-lg mb-4 text-white bg-primary rounded-lg p-3">Consent And Verification</h3>
          <div className="space-y-4 text-sm leading-relaxed">
            <p className="font-semibold text-gray-800">27. By Signing Below, I Understand And Agree To The Following Conditions Of License:</p>
            
            <p>Each licensee shall comply with all applicable Tribal laws and regulations governing the manufacture, distribution, and sale of liquor within the exterior boundaries of the Colorado River Indian Tribes' Reservation, subject to all applicable Tribal and federal law governing the manufacture, distribution, and sale of liquor. The licensee is required to comply with all such additional tribal laws as such may be enacted in the future as well as any and all regulations that may require business activities that are regulated.</p>
            
            <p>Each licensee consents to the jurisdiction of the Tribal Court as to any cause of action arising in connection with the sale of liquor under this license. Every license to sell liquor within the jurisdiction of the Reservation commits the licensee to the service of process in the Tribal Court with respect to all actions over which the Tribal Court has subject matter jurisdiction, in accordance with the rules and procedures as approved by the Tribal Court.</p>
            
            <p>Each retail licensee understands and agrees that retailers are not be permitted to buy liquor, beer or wine for resale except from wholesalers licensed by the Tribes' Liquor Control Board.</p>
            
            <p>Each licensee shall respond in a timely manner to requests by the Liquor Control Board for information about the licensee's business.</p>
            
            <p className="font-semibold mt-4">I Declare That I Have Examined This Application And To The Best Of My Knowledge And Belief, It Is True And Correct. I Swear Or Affirm That I Have Read And Will Comply With All Tribal Laws Applicable To My Business And Consent To The Jurisdiction Of The Tribal Court Of The Colorado River Indian Tribes And Service Of Process In Matters Arising From The Conduct Of Liquor Sales.</p>
            
            <div className="mt-8 bg-gray-50 rounded-lg p-6 border border-gray-200">
              <label className="font-bold block mb-3 text-gray-800">Sign Below To Certify The Accuracy Of This Application:</label>
              
              {/* Signature Canvas Container */}
              <div className="border-2 border-primary rounded-lg p-4 bg-white mb-4" style={{ minHeight: '200px' }}>
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="#1e40af"
                  backgroundColor="rgb(255, 255, 255)"
                  canvasProps={{
                    className: 'signature-canvas w-full h-full border border-gray-300 rounded bg-white cursor-crosshair',
                    style: {
                      width: '100%',
                      height: '100%',
                      minHeight: '150px'
                    }
                  }}
                  onBegin={handleSignatureBegin}
                  onEnd={handleSignatureEnd}
                  clearOnResize={false}
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <div className="text-sm">
                  {isSigned ? (
                    <span className="text-green-600 font-bold flex items-center gap-2"><span className="text-lg">✓</span> Signature provided</span>
                  ) : (
                    <span className="text-red-600 font-semibold">⚠ Please sign above</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 text-sm font-semibold"
                >
                  Clear Signature
                </button>
              </div>

              {/* Printed Name and Date Fields */}
              <div className="border-t border-gray-300 pt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Printed Name: <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={printedName}
                      onChange={(e) => setPrintedName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Date Signed:
                    </label>
                    <input
                      type="text"
                      value={currentDate}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-gray-100 font-semibold text-gray-700 cursor-not-allowed text-sm"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 italic">
                  This date is automatically set to today's date and cannot be modified
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border-2 border-red-400 shadow-md p-6">
          <p className="text-center font-semibold text-red-700 mb-3 text-base">
            Please make check/money order payable to "CRIT Liquor Control Board." <span className="font-bold text-red-900">No Cash Accepted.</span>
          </p>
          <p className="text-center text-sm text-gray-700 mb-4">Please mail or return your completed application and payment to:</p>
          <div className="text-center text-sm bg-white rounded-lg p-4 border border-gray-200">
            <p className="font-bold text-gray-800">CRIT Liquor Control Board</p>
            <p className="text-gray-700">c/o CRIT Department of Revenue and Finance</p>
            <p className="text-gray-700">26600 Mohave Road</p>
            <p className="text-gray-700">Parker, Arizona 85344</p>
            <p className="text-gray-700 mt-2"><strong>Phone:</strong> (928) 669-1336</p>
            <p className="text-gray-700"><strong>Email:</strong> taxinfo@crit-nsn.gov</p>
          </div>
        </div>

      

        {/* Submit Button */}
        <div className="flex justify-center pt-8">
          <button
            type="submit"
            disabled={!isSigned || !printedName.trim() || isSubmitting}
            className="bg-primary text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-opacity-90 transition-all duration-200 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : (
              isSigned && printedName.trim() ? 'Submit Application' : 'Please Complete All Required Fields'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LiquorLicense;